import type { SharedLoadout } from './loadout-share';

/**
 * Your item stats (website roadmap S39 — torn.report's Item Stats): your own weapons and armor with their usage over time
 * (hits, critical hits, damage, rounds fired, hits taken…), who first owned each and when it was made, its bonuses — every
 * read kept as a snapshot per item, two saved dates side by side, new items marked. Pure and browser-safe; tested in
 * packages/shared/test/item-stats.test.ts.
 *
 * Where it comes from (Torn's v2 API, checked against Torn's OpenAPI 6.13.x as the torn-api crate carries it):
 *  - `user/equipment` (Minimal): what you have on — `equipment: [{ id, uid, name, type, sub_type, slot, stats: { damage,
 *    accuracy, armor, quality }, bonuses: [{ id, title, description, value }], rarity, mods, ammo }]`;
 *  - `user/inventory?cat=Primary|Secondary|Melee|Defensive` (Minimal, Torn caches it an hour per category): your other
 *    copies — `inventory: [{ id, uid, name, amount, equipped, faction_owned }]` (uid only on unique items);
 *  - `torn/{uids}/itemdetails` (Public): a copy's stats, bonuses and rarity, like `equipment`;
 *  - `torn/{uids}/itemstats` (Public): a copy's usage — `[{ id, uid, name, type, sub_type, stats: [{ id, title, value }] }]`,
 *    titles: Damage, Rounds fired, Hits, Misses, Damage taken, Reloads, Highest damage, Hits received, Most damage taken,
 *    Damage mitigated, Most damage mitigated, Finishing hits, Critical hits, First owner, First faction owner, Time created,
 *    Respect earned. The answer's list key isn't certain (the crate names it `itemdetails`): any list of such rows is read.
 * And your own Items page as the extension sees it while you view it (Torn's getEquippedItems answer: what you have on,
 * with damage, accuracy, armor, bonuses and colour — no uid, no usage): kept as "page" snapshots.
 *
 * A snapshot (`ItemSnap`) is kept on the Omni-Torn server for players on terms 19 (own-history stream `items`, one entry
 * per item per read) and in the browser below it.
 */

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const list = (x: unknown): unknown[] => (Array.isArray(x) ? x : obj(x) ? Object.values(x as Obj) : []);
const num = (x: unknown): number | null => {
  if (typeof x === 'number' && Number.isFinite(x)) return x;
  if (typeof x === 'string' && x.trim() !== '' && Number.isFinite(Number(x.replace(/,/g, '')))) return Number(x.replace(/,/g, ''));
  return null;
};
const posInt = (x: unknown): number | null => {
  const v = num(x);
  return v !== null && Number.isInteger(v) && v > 0 && v < 1e13 ? v : null;
};
const str = (x: unknown, max = 80): string | null => (typeof x === 'string' && x.trim() ? x.trim().slice(0, max) : null);

// ---------------------------------------------------------------- what a snapshot holds

/** Torn's usage stats on a copy, by our name; `title` is Torn's. */
export const ITEM_USAGE = [
  { key: 'hits', title: 'Hits', label: 'Hits', weapon: true },
  { key: 'criticalHits', title: 'Critical hits', label: 'Critical hits', weapon: true },
  { key: 'damage', title: 'Damage', label: 'Damage', weapon: true },
  { key: 'roundsFired', title: 'Rounds fired', label: 'Rounds fired', weapon: true },
  { key: 'misses', title: 'Misses', label: 'Misses', weapon: true },
  { key: 'reloads', title: 'Reloads', label: 'Reloads', weapon: true },
  { key: 'finishingHits', title: 'Finishing hits', label: 'Finishing hits', weapon: true },
  { key: 'highestDamage', title: 'Highest damage', label: 'Highest damage', weapon: true },
  { key: 'respectEarned', title: 'Respect earned', label: 'Respect earned', weapon: true },
  { key: 'hitsReceived', title: 'Hits received', label: 'Hits taken', weapon: false },
  { key: 'damageTaken', title: 'Damage taken', label: 'Damage taken', weapon: false },
  { key: 'damageMitigated', title: 'Damage mitigated', label: 'Damage mitigated', weapon: false },
  { key: 'mostDamageTaken', title: 'Most damage taken', label: 'Most damage taken', weapon: false },
  { key: 'mostDamageMitigated', title: 'Most damage mitigated', label: 'Most damage mitigated', weapon: false },
] as const;
export type ItemUsageKey = (typeof ITEM_USAGE)[number]['key'];
const USAGE_KEYS = new Set<string>(ITEM_USAGE.map((u) => u.key));
/** Torn's title (any case, any spacing) → our key. */
const BY_TITLE = new Map<string, ItemUsageKey>(ITEM_USAGE.map((u) => [u.title.toLowerCase().replace(/[^a-z]/g, ''), u.key]));
const titleKey = (t: string) => t.toLowerCase().replace(/[^a-z]/g, '');

export type ItemKind = 'weapon' | 'armor';
export type ItemRarity = 'yellow' | 'orange' | 'red';
export interface ItemBonus {
  title: string;
  value: number | null;
}

/** One item as one read saw it (kept as is: the own-history stream `items`). Times in Torn's seconds. */
export interface ItemSnap {
  /** When it was read (s). */
  timestamp: number;
  /** Torn's API (with your key) or your own Items page (the extension, while you viewed it). */
  source: 'api' | 'page';
  /** The copy's unique id (null: from the Items page, which doesn't say). */
  uid: number | null;
  item_id: number;
  name: string;
  kind: ItemKind;
  /** Primary / Secondary / Melee / Temporary for weapons. */
  sub_type?: string;
  /** Equip slot name (Primary … Boots) when it's on. */
  slot?: string;
  equipped?: boolean;
  stats?: { damage?: number; accuracy?: number; armor?: number; quality?: number };
  bonuses?: ItemBonus[];
  rarity?: ItemRarity;
  usage?: Partial<Record<ItemUsageKey, number>>;
  first_owner?: number;
  first_faction_owner?: number;
  /** When the copy was made (s). */
  created?: number;
}

/** Torn's equip slots by number (v2 `slot`, the Items page's `equipSlot`). */
const SLOT_NAMES: Record<number, string> = { 1: 'Primary', 2: 'Secondary', 3: 'Melee', 5: 'Temporary', 6: 'Helmet', 4: 'Body', 7: 'Pants', 9: 'Gloves', 8: 'Boots' };
const SLOT_CODE: Record<string, number> = Object.fromEntries(Object.entries(SLOT_NAMES).map(([k, v]) => [v, Number(k)]));
const WEAPON_SUBS = new Set(['Primary', 'Secondary', 'Melee', 'Temporary']);
const ARMOR_SLOTS = new Set(['Helmet', 'Body', 'Pants', 'Gloves', 'Boots']);
/** The inventory categories read for weapons and armor (Torn's `cat`), in the order the page offers them. */
export const ITEM_STAT_CATEGORIES = ['Primary', 'Secondary', 'Melee', 'Defensive'] as const;
export type ItemStatCategory = (typeof ITEM_STAT_CATEGORIES)[number];
/** At most this many uids in one itemstats / itemdetails read. */
export const ITEM_STATS_BATCH = 50;

function kindOf(type: unknown, sub: unknown, slot: string | undefined): ItemKind | null {
  const t = typeof type === 'string' ? type.toLowerCase() : '';
  if (t === 'weapon' || (typeof sub === 'string' && WEAPON_SUBS.has(sub)) || (slot && WEAPON_SUBS.has(slot))) return 'weapon';
  if (t === 'armor' || t === 'defensive' || (slot && ARMOR_SLOTS.has(slot))) return 'armor';
  return null;
}

function rarityOf(x: unknown): ItemRarity | undefined {
  if (typeof x === 'string') {
    const w = /(yellow|orange|red)/i.exec(x)?.[1]?.toLowerCase();
    if (w) return w as ItemRarity;
  }
  if (typeof x === 'number') return ({ 1: 'yellow', 2: 'orange', 3: 'red' } as Record<number, ItemRarity>)[x];
  return undefined;
}

function bonusesOf(x: unknown): ItemBonus[] {
  return list(x)
    .map((b) => {
      const o = obj(b);
      const title = str(o?.title, 40);
      return title ? { title, value: num(o?.value) } : null;
    })
    .filter((b): b is ItemBonus => !!b)
    .slice(0, 4);
}

function statsOf(x: unknown): ItemSnap['stats'] | undefined {
  const o = obj(x);
  if (!o) return undefined;
  const out: NonNullable<ItemSnap['stats']> = {};
  for (const k of ['damage', 'accuracy', 'armor', 'quality'] as const) {
    const v = num(o[k]);
    if (v !== null && v >= 0 && v < 1e6) out[k] = v;
  }
  return Object.keys(out).length ? out : undefined;
}

// ---------------------------------------------------------------- Torn's answers

/** What a read told about one copy (any of the four answers), before it becomes a snapshot. */
export interface ItemInfo {
  uid: number;
  item_id: number;
  name: string;
  kind: ItemKind | null;
  sub_type?: string;
  slot?: string;
  equipped?: boolean;
  stats?: ItemSnap['stats'];
  bonuses?: ItemBonus[];
  rarity?: ItemRarity;
}

function infoOf(o: Obj, equipped?: boolean): ItemInfo | null {
  const uid = posInt(o.uid);
  const itemId = posInt(o.id ?? o.item_id ?? o.ID);
  const name = str(o.name);
  if (!uid || !itemId || !name) return null;
  const slotNo = num(o.slot);
  const slot = slotNo !== null ? SLOT_NAMES[slotNo] : undefined;
  const sub = str(o.sub_type, 20) ?? undefined;
  const stats = statsOf(o.stats);
  const bonuses = o.bonuses !== undefined ? bonusesOf(o.bonuses) : undefined;
  const rarity = rarityOf(o.rarity);
  return {
    uid,
    item_id: itemId,
    name,
    kind: kindOf(o.type, sub, slot),
    ...(sub ? { sub_type: sub } : {}),
    ...(slot ? { slot } : {}),
    ...(equipped !== undefined ? { equipped } : typeof o.equipped === 'boolean' ? { equipped: o.equipped } : {}),
    ...(stats ? { stats } : {}),
    ...(bonuses ? { bonuses } : {}),
    ...(rarity ? { rarity } : {}),
  };
}

/** v2 `user/equipment`: your weapons and armor on now (clothing left out). */
export function readEquipment(body: unknown): ItemInfo[] {
  return list(obj(body)?.equipment)
    .map((x) => (obj(x) ? infoOf(obj(x)!, true) : null))
    .filter((i): i is ItemInfo => !!i && i.kind !== null);
}

/** v2 `user/inventory?cat=…`: your copies with a uid in that category; `complete` when Torn gave the whole category. */
export function readInventory(body: unknown, cat: ItemStatCategory, limit = 250): { items: ItemInfo[]; complete: boolean } {
  const b = obj(body);
  const raw = Array.isArray(b?.inventory) ? (b!.inventory as unknown[]) : list(obj(b?.inventory)?.items);
  const items: ItemInfo[] = [];
  for (const x of raw) {
    const o = obj(x);
    if (!o || o.faction_owned === true) continue;
    const i = infoOf(o);
    if (!i) continue;
    i.kind ??= cat === 'Defensive' ? 'armor' : 'weapon';
    if (cat !== 'Defensive' && !i.sub_type) i.sub_type = cat;
    items.push(i);
  }
  const next = obj(obj(b?._metadata)?.links)?.next;
  return { items, complete: raw.length < limit && !next };
}

/** The rows of an itemdetails / itemstats answer, whatever Torn names the list (one row, an array or by uid). */
function rowsOf(body: unknown, keys: readonly string[]): Obj[] {
  const b = obj(body);
  if (!b) return [];
  for (const k of keys) {
    const v = b[k];
    if (Array.isArray(v)) return v.map(obj).filter((o): o is Obj => !!o);
    const o = obj(v);
    if (o) return o.uid !== undefined ? [o] : Object.values(o).map(obj).filter((x): x is Obj => !!x);
  }
  return [];
}

/** v2 `torn/{uids}/itemdetails`: each copy's stats, bonuses and rarity. */
export function readItemDetails(body: unknown): ItemInfo[] {
  return rowsOf(body, ['itemdetails', 'items'])
    .filter((o) => o.stats === undefined || !Array.isArray(o.stats))
    .map((o) => infoOf(o))
    .filter((i): i is ItemInfo => !!i);
}

export interface ItemUsage {
  uid: number;
  item_id: number;
  name: string;
  kind: ItemKind | null;
  sub_type?: string;
  usage: Partial<Record<ItemUsageKey, number>>;
  first_owner?: number;
  first_faction_owner?: number;
  created?: number;
}

/** v2 `torn/{uids}/itemstats`: each copy's usage, first owners and when it was made (rows whose `stats` is a list). */
export function readItemStats(body: unknown): ItemUsage[] {
  const out: ItemUsage[] = [];
  for (const o of rowsOf(body, ['itemstats', 'itemdetails', 'items'])) {
    if (!Array.isArray(o.stats)) continue;
    const uid = posInt(o.uid);
    const itemId = posInt(o.id ?? o.item_id);
    if (!uid || !itemId) continue;
    const u: ItemUsage = { uid, item_id: itemId, name: str(o.name) ?? `Item ${itemId}`, kind: kindOf(o.type, o.sub_type, undefined), usage: {} };
    const sub = str(o.sub_type, 20);
    if (sub) u.sub_type = sub;
    for (const s of o.stats) {
      const so = obj(s);
      const title = typeof so?.title === 'string' ? titleKey(so.title) : '';
      const v = num(so?.value);
      if (!title || v === null || v < 0) continue;
      if (title === 'firstowner') v > 0 && (u.first_owner = Math.round(v));
      else if (title === 'firstfactionowner') v > 0 && (u.first_faction_owner = Math.round(v));
      else if (title === 'timecreated') v > 0 && (u.created = v > 1e11 ? Math.round(v / 1000) : Math.round(v));
      else {
        const k = BY_TITLE.get(title);
        if (k) u.usage[k] = v;
      }
    }
    out.push(u);
  }
  return out;
}

/**
 * The snapshots of one usage read (one per copy it covered), with what's known of each copy (equipment, inventory,
 * details — the newest first wins per field).
 */
export function snapsFromRead(at: number, usage: readonly ItemUsage[], known: ReadonlyMap<number, ItemInfo>): ItemSnap[] {
  const ts = Math.floor(at / 1000);
  const out: ItemSnap[] = [];
  for (const u of usage) {
    const k = known.get(u.uid);
    const kind = k?.kind ?? u.kind;
    if (!kind) continue;
    const s: ItemSnap = { timestamp: ts, source: 'api', uid: u.uid, item_id: u.item_id, name: k?.name ?? u.name, kind };
    const sub = k?.sub_type ?? u.sub_type;
    if (sub) s.sub_type = sub;
    if (k?.slot) s.slot = k.slot;
    if (k?.equipped !== undefined) s.equipped = k.equipped;
    if (k?.stats) s.stats = k.stats;
    if (k?.bonuses?.length) s.bonuses = k.bonuses;
    if (k?.rarity) s.rarity = k.rarity;
    if (Object.keys(u.usage).length) s.usage = { ...u.usage };
    if (u.first_owner) s.first_owner = u.first_owner;
    if (u.first_faction_owner) s.first_faction_owner = u.first_faction_owner;
    if (u.created) s.created = u.created;
    out.push(s);
  }
  return out;
}

/** "Burn 39%", "Disarm 5 turns", "Irradiate" (the extension's loadout text) → Torn's title and value. */
export function parseBonusText(t: string): ItemBonus | null {
  const m = /^(.*?)\s+(\d+(?:\.\d+)?)\s*(?:%|turns?)?$/.exec(t.trim());
  if (m && m[1]) return { title: m[1].slice(0, 40), value: Number(m[2]) };
  return t.trim() ? { title: t.trim().slice(0, 40), value: null } : null;
}

/** Your loadout as your own Items page showed it (the extension, while you viewed it) → "page" snapshots. */
export function snapsFromPage(at: number, loadout: SharedLoadout): ItemSnap[] {
  const ts = Math.floor(at / 1000);
  const out: ItemSnap[] = [];
  for (const i of loadout.items) {
    const kind = kindOf(null, null, i.slot);
    if (!kind) continue;
    const s: ItemSnap = { timestamp: ts, source: 'page', uid: null, item_id: i.itemId, name: i.name, kind, slot: i.slot, equipped: true };
    if (kind === 'weapon') s.sub_type = i.slot;
    const stats = statsOf({ damage: i.damage, accuracy: i.accuracy, armor: i.armor });
    if (stats) s.stats = stats;
    const bonuses = (i.bonuses ?? []).map(parseBonusText).filter((b): b is ItemBonus => !!b);
    if (bonuses.length) s.bonuses = bonuses;
    if (i.rarity) s.rarity = i.rarity;
    out.push(s);
  }
  return out;
}

/** A snapshot's id in the stream (Torn's id characters): `<uid>-<time>`, or `p<item>-<slot>-<time>` from the Items page. */
export function itemSnapId(s: ItemSnap): string {
  return s.uid ? `${s.uid}-${s.timestamp}` : `p${s.item_id}-${SLOT_CODE[s.slot ?? ''] ?? 0}-${s.timestamp}`;
}

/** Snapshots as the own-history upload sends them (id → snapshot). */
export function itemSnapEntries(snaps: readonly ItemSnap[]): Record<string, ItemSnap> {
  return Object.fromEntries(snaps.map((s) => [itemSnapId(s), s]));
}

/**
 * A snapshot as it may be kept, or null: only the fields above, numbers in range, short strings — the server keeps nothing
 * else of an upload (own-history.ts `cleanOwnUpload`), and the page reads back only these.
 */
export function cleanItemSnap(x: unknown, now = Date.now()): ItemSnap | null {
  const o = obj(x);
  if (!o) return null;
  const ts = num(o.timestamp);
  const itemId = posInt(o.item_id);
  const name = str(o.name);
  const kind = o.kind === 'weapon' || o.kind === 'armor' ? o.kind : null;
  const source = o.source === 'api' || o.source === 'page' ? o.source : null;
  if (ts === null || !Number.isInteger(ts) || ts < 1_000_000_000 || ts * 1000 > now + 60_000 || !itemId || !name || !kind || !source) return null;
  const uid = o.uid === null || o.uid === undefined ? null : posInt(o.uid);
  if (o.uid !== null && o.uid !== undefined && !uid) return null;
  if (source === 'api' && !uid) return null;
  const s: ItemSnap = { timestamp: ts, source, uid, item_id: itemId, name, kind };
  const sub = str(o.sub_type, 20);
  if (sub) s.sub_type = sub;
  const slot = str(o.slot, 20);
  if (slot && SLOT_CODE[slot]) s.slot = slot;
  if (typeof o.equipped === 'boolean') s.equipped = o.equipped;
  const stats = statsOf(o.stats);
  if (stats) s.stats = stats;
  const bonuses = bonusesOf(o.bonuses);
  if (bonuses.length) s.bonuses = bonuses;
  const rarity = rarityOf(o.rarity);
  if (rarity) s.rarity = rarity;
  const u = obj(o.usage);
  if (u) {
    const usage: Partial<Record<ItemUsageKey, number>> = {};
    for (const [k, v] of Object.entries(u)) {
      const n = num(v);
      if (USAGE_KEYS.has(k) && n !== null && n >= 0 && n < 1e15) usage[k as ItemUsageKey] = n;
    }
    if (Object.keys(usage).length) s.usage = usage;
  }
  for (const k of ['first_owner', 'first_faction_owner'] as const) {
    const v = posInt(o[k]);
    if (v && v < 1e9) s[k] = v;
  }
  const created = posInt(o.created);
  if (created && created < ts + 60) s.created = created;
  return s;
}

// ---------------------------------------------------------------- the report

/** One item over time: its snapshots oldest first. `key`: `u<uid>` or, from the Items page only, `p<item>-<slot>`. */
export interface ItemTrack {
  key: string;
  uid: number | null;
  item_id: number;
  name: string;
  kind: ItemKind;
  snaps: ItemSnap[];
  /** The newest snapshot (with the newest known stats, bonuses and rarity filled in from older ones). */
  latest: ItemSnap;
  /** First seen (ms). */
  firstSeen: number;
  /** New: not in any reading before the newest one (and there was one), or made within the last week. */
  isNew: boolean;
}

const trackKey = (s: ItemSnap) => (s.uid ? `u${s.uid}` : `p${s.item_id}-${s.slot ?? ''}`);

/**
 * Every item over time. Items-page snapshots join the API copy of the same item when exactly one copy of that item is
 * known (they carry no uid); else they are an item of their own. `now` decides "made within the last week".
 */
export function itemTracks(snaps: readonly ItemSnap[], now = Date.now()): ItemTrack[] {
  const byUidItem = new Map<number, Set<number>>();
  for (const s of snaps) if (s.uid) (byUidItem.get(s.item_id) ?? byUidItem.set(s.item_id, new Set()).get(s.item_id)!).add(s.uid);
  const groups = new Map<string, ItemSnap[]>();
  for (const s of snaps) {
    const copies = !s.uid ? byUidItem.get(s.item_id) : undefined;
    const key = copies?.size === 1 ? `u${[...copies][0]}` : trackKey(s);
    (groups.get(key) ?? groups.set(key, []).get(key)!).push(s);
  }
  // Readings (API use reads, a few minutes apart counted as one): what the newest one brought.
  const readings = readingSpans(snaps.filter((s) => s.source === 'api'));
  const newest = readings[readings.length - 1]?.from ?? null;
  const tracks: ItemTrack[] = [];
  for (const [key, list0] of groups) {
    const list = [...list0].sort((a, b) => a.timestamp - b.timestamp || (a.source === b.source ? 0 : a.source === 'api' ? 1 : -1));
    const api = list.filter((s) => s.source === 'api');
    const last = api[api.length - 1] ?? list[list.length - 1]!;
    // Fill in what the newest read didn't carry (a usage read of a copy whose details were read earlier).
    const latest: ItemSnap = { ...last };
    for (let i = list.length - 1; i >= 0; i--) {
      const s = list[i]!;
      if (!latest.stats && s.stats) latest.stats = s.stats;
      if (!latest.bonuses && s.bonuses) latest.bonuses = s.bonuses;
      if (!latest.rarity && s.rarity) latest.rarity = s.rarity;
      if (!latest.sub_type && s.sub_type) latest.sub_type = s.sub_type;
      if (latest.created === undefined && s.created !== undefined) latest.created = s.created;
      if (latest.first_owner === undefined && s.first_owner !== undefined) latest.first_owner = s.first_owner;
    }
    const firstSeen = list[0]!.timestamp * 1000;
    const firstApi = api[0]?.timestamp ?? null;
    const madeRecently = latest.created !== undefined && now - latest.created * 1000 < 7 * 86_400_000;
    const newInRead = newest !== null && firstApi !== null && firstApi >= newest && readings.length > 1 && !list.some((s) => s.timestamp < newest);
    tracks.push({ key, uid: last.uid, item_id: last.item_id, name: last.name, kind: last.kind, snaps: list, latest, firstSeen, isNew: newInRead || madeRecently });
  }
  return tracks.sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'weapon' ? -1 : 1) || Number(b.latest.equipped === true) - Number(a.latest.equipped === true) || a.name.localeCompare(b.name) || (a.uid ?? 0) - (b.uid ?? 0));
}

/** Reads within this long of a reading's first one are the same reading (one click after another: a category, then its use). */
export const READING_GAP_S = 10 * 60;

/** Readings (s, oldest first): snapshots' times, those within READING_GAP_S of a reading's start joined into it. */
export function readingSpans(snaps: readonly ItemSnap[]): Array<{ from: number; to: number }> {
  const out: Array<{ from: number; to: number }> = [];
  for (const t of [...new Set(snaps.map((s) => s.timestamp))].sort((a, b) => a - b)) {
    const last = out[out.length - 1];
    if (last && t - last.from <= READING_GAP_S) last.to = t;
    else out.push({ from: t, to: t });
  }
  return out;
}

/**
 * The dates readings were saved on (ms, newest first): each reading's end — every use read and every Items-page view the
 * extension saw, reads minutes apart (one click after another) counted as one.
 */
export function savedDates(snaps: readonly ItemSnap[]): number[] {
  return readingSpans(snaps).map((r) => r.to * 1000).reverse();
}


/** An item's newest snapshot at or before a time (ms), from the API when there is one then. */
export function snapAt(track: ItemTrack, at: number): ItemSnap | null {
  let best: ItemSnap | null = null;
  for (const s of track.snaps) if (s.timestamp * 1000 <= at && (!best || s.timestamp > best.timestamp || (s.timestamp === best.timestamp && s.source === 'api'))) best = s;
  return best;
}

/** One measure of a snapshot (usage, or damage / accuracy / armor / quality). */
export type ItemMeasure = ItemUsageKey | 'statDamage' | 'accuracy' | 'armor' | 'quality';
export function measureOf(s: ItemSnap | null, m: ItemMeasure): number | null {
  if (!s) return null;
  if (m === 'statDamage') return s.stats?.damage ?? null;
  if (m === 'accuracy' || m === 'armor' || m === 'quality') return s.stats?.[m] ?? null;
  return s.usage?.[m] ?? null;
}

/** Critical hits as a share of hits (0–1), or null. */
export function critRate(s: ItemSnap | null): number | null {
  const hits = s?.usage?.hits;
  const crits = s?.usage?.criticalHits;
  return hits && crits !== undefined ? crits / hits : null;
}

export interface ItemCompareRow {
  track: ItemTrack;
  a: ItemSnap | null;
  b: ItemSnap | null;
  /** Measure → [at A, at B, B − A]. */
  values: Partial<Record<ItemMeasure, [number | null, number | null, number | null]>>;
  /** Had at B but not at A. */
  added: boolean;
  /** Had at A, not read at B (sold, traded, or that read didn't cover it). */
  gone: boolean;
}

/** Two saved dates side by side (A before B): each item's measures then and the change. */
export function compareDates(tracks: readonly ItemTrack[], a: number, b: number, measures: readonly ItemMeasure[]): ItemCompareRow[] {
  const [from, to] = a <= b ? [a, b] : [b, a];
  const rows: ItemCompareRow[] = [];
  for (const t of tracks) {
    const sa = snapAt(t, from);
    const sb = snapAt(t, to);
    if (!sa && !sb) continue;
    const values: ItemCompareRow['values'] = {};
    for (const m of measures) {
      const x = measureOf(sa, m);
      const y = measureOf(sb, m);
      if (x === null && y === null) continue;
      values[m] = [x, y, x !== null && y !== null ? y - x : null];
    }
    const readAtB = !!sb && sb.timestamp * 1000 === to;
    rows.push({ track: t, a: sa, b: sb, values, added: !sa && !!sb, gone: !!sa && !readAtB && t.snaps.every((s) => s.timestamp * 1000 <= from || s.timestamp * 1000 > to) });
  }
  return rows;
}

/** A measure over time for one item (ms, value), its API snapshots only — for the chart. */
export function measureSeries(track: ItemTrack, m: ItemMeasure): Array<{ at: number; v: number }> {
  return track.snaps
    .filter((s) => s.source === 'api' || m === 'statDamage' || m === 'accuracy' || m === 'armor')
    .map((s) => ({ at: s.timestamp * 1000, v: measureOf(s, m) }))
    .filter((p): p is { at: number; v: number } => p.v !== null);
}

/** Uids known (from any read or snapshot) whose usage this visit hasn't read yet. */
export function uidsToRead(known: Iterable<number>, readNow: ReadonlySet<number>): number[] {
  return [...new Set(known)].filter((u) => !readNow.has(u));
}

/** CSV of the newest snapshot of each item. */
export function itemsCsvRows(tracks: readonly ItemTrack[]): { header: string[]; rows: Array<Array<string | number | null>> } {
  // (Torn's damage stat of a weapon is `weapon_damage`; `damage` is the damage it dealt.)
  const header = ['uid', 'item_id', 'name', 'kind', 'type', 'equipped', 'rarity', 'weapon_damage', 'accuracy', 'armor', 'quality', 'bonuses', ...ITEM_USAGE.map((u) => u.key), 'first_owner', 'first_faction_owner', 'created_utc', 'read_utc', 'source'];
  const iso = (s: number | undefined) => (s ? new Date(s * 1000).toISOString() : null);
  const rows = tracks.map((t) => {
    const s = t.latest;
    return [s.uid, s.item_id, s.name, s.kind, s.sub_type ?? s.slot ?? null, s.equipped ? 'yes' : 'no', s.rarity ?? null, s.stats?.damage ?? null, s.stats?.accuracy ?? null, s.stats?.armor ?? null, s.stats?.quality ?? null, (s.bonuses ?? []).map((b) => (b.value === null ? b.title : `${b.title} ${b.value}`)).join('; ') || null, ...ITEM_USAGE.map((u) => s.usage?.[u.key] ?? null), s.first_owner ?? null, s.first_faction_owner ?? null, iso(s.created), iso(s.timestamp), s.source];
  });
  return { header, rows };
}
